import { Box, Typography, Chip, Stepper, Step, StepLabel, StepContent } from '@mui/material';
import { vortxColors, vortxTypography, chartColors } from '@vortx/shared-ui';
import AnimatedMetric from '../AnimatedMetric';
import StepSlide, { StatCard, SplitView, DemoBullets } from '../StepSlide';
import { retroColors, demoColors } from '../theme';
import {
    SearchOutlined,
    DesignServicesOutlined,
    SmartToyOutlined,
    TrendingUpOutlined,
    PlayArrowOutlined,
    DashboardOutlined,
    AttachMoneyOutlined,
    LoopOutlined,
    TimelineOutlined,
    EvStationOutlined,
    AcUnitOutlined,
    AnalyticsOutlined,
    RouterOutlined,
} from '@mui/icons-material';

/**
 * Step 3: "Vortx: Intelligence + Control for Distributed Energy"
 */
export function Step3_PlatformOverview({ visible }) {
    const capabilities = [
        {
            label: 'Design',
            product: 'Site DNA',
            icon: <DesignServicesOutlined />,
            desc: 'Screen sites, size equipment, model revenue stacks, generate bankable financials — before a dollar is committed.',
            color: vortxColors.gold,
        },
        {
            label: 'Control',
            product: 'Flux EMS',
            icon: <SmartToyOutlined />,
            desc: 'On-site autonomous energy controller — dispatches every asset every 15 minutes, maximizes revenue, maintains compliance.',
            color: demoColors.actPlatform,
        },
        {
            label: 'Operate',
            product: 'Portfolio',
            icon: <DashboardOutlined />,
            desc: 'Aggregate sites, track performance, automate reporting, coordinate with utilities and investors.',
            color: chartColors.hvac,
        },
    ];

    const workflowSteps = [
        { label: 'Screen', desc: 'Enter address → AI pulls utility capacity, distribution voltage, interconnection costs' },
        { label: 'Design', desc: 'Site parameters → Julia solver optimizes BESS sizing + dispatch strategy' },
        { label: 'Tariff Analysis', desc: '10,000+ utility tariffs → demand charge structure → revenue stack modeling' },
        { label: 'Optimization', desc: 'MILP solver co-optimizes across RA, arbitrage, demand response, frequency regulation' },
        { label: 'Financials', desc: 'Development yield, 25-year NPV, revenue breakdown by stream' },
        { label: 'Deploy', desc: 'Flux goes live at commissioning → autonomous dispatch from day one' },
        { label: 'Operate', desc: '35,040 optimal decisions per year per site. No human in the loop.' },
    ];

    return (
        <StepSlide
            title="Intelligence + Control for Distributed Energy"
            subtitle="Vortx provides three capabilities for any distributed energy site."
            behindTheScenes="Site DNA designs the system. Flux runs it. The same platform — same solver, same controller, same reporting — works for a powered land site, a C&I solar+storage project, a fleet depot, or a cold chain facility. The intelligence is the product. The site type is a configuration."
            keyStat="Design in hours, not weeks. Dispatch 35,040 decisions per year. Same platform, any distributed energy site."
            visible={visible}
        >
            {/* Three capabilities */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: 3,
                mb: 4,
            }}>
                {capabilities.map((cap, i) => (
                    <Box key={i} sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: retroColors.surface,
                        border: `1px solid ${cap.color}33`,
                        borderTop: `3px solid ${cap.color}`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Box sx={{ color: cap.color }}>{cap.icon}</Box>
                            <Typography sx={{
                                fontFamily: vortxTypography.heading,
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                color: vortxColors.white,
                            }}>
                                {cap.label}
                            </Typography>
                            <Chip
                                label={cap.product}
                                size="small"
                                sx={{
                                    bgcolor: `${cap.color}15`,
                                    color: cap.color,
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.6rem',
                                }}
                            />
                        </Box>
                        <Typography sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.85rem',
                            color: retroColors.dimmed,
                            lineHeight: 1.5,
                        }}>
                            {cap.desc}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Workflow pipeline */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                {workflowSteps.map((step, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1,
                            bgcolor: retroColors.surface,
                            border: `1px solid rgba(178, 140, 80, 0.15)`,
                            cursor: 'default',
                            '&:hover': {
                                borderColor: vortxColors.gold,
                                '& .workflow-desc': { maxHeight: 60, opacity: 1 },
                            },
                        }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                color: vortxColors.gold,
                                fontWeight: 600,
                            }}>
                                {i + 1}. {step.label}
                            </Typography>
                        </Box>
                        {i < workflowSteps.length - 1 && (
                            <Typography sx={{ color: retroColors.muted, mx: 0.5, fontSize: '0.8rem' }}>→</Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </StepSlide>
    );
}

/**
 * Step 4: "The Business Model: Asset-Light, Software-Heavy"
 */
export function Step4_BusinessModel({ visible }) {
    const streams = [
        {
            label: 'Development Fee',
            amount: '$200K',
            per: 'per site',
            type: 'One-time',
            desc: 'Engineering, BESS sizing, interconnection, permitting',
            color: demoColors.devFee,
        },
        {
            label: 'Flux-as-a-Service',
            amount: '$3.00',
            per: '/kW-month',
            type: 'Recurring',
            desc: 'Starts at commissioning. Independent of tenant occupancy.',
            color: demoColors.fluxSaas,
        },
        {
            label: 'Asset Management',
            amount: '3%',
            per: 'of gross revenue',
            type: 'Recurring',
            desc: 'Portfolio oversight, utility/tenant coordination, investor reporting',
            color: demoColors.assetMgmt,
        },
    ];

    return (
        <StepSlide
            title="Asset-Light, Software-Heavy"
            subtitle="Vortx deploys intelligence, not capital. Three revenue streams — two recurring."
            keyStat="Recurring gross margin: 96%+. Each incremental site: ~$470K ARR."
            visible={visible}
        >
            {/* Revenue streams */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: 3,
                mb: 4,
            }}>
                {streams.map((stream, i) => (
                    <Box key={i} sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: retroColors.surface,
                        border: `1px solid ${stream.color}33`,
                        position: 'relative',
                        overflow: 'hidden',
                    }}>
                        <Chip
                            label={stream.type}
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                bgcolor: stream.type === 'Recurring' ? 'rgba(95, 212, 196, 0.1)' : 'rgba(178, 140, 80, 0.1)',
                                color: stream.type === 'Recurring' ? demoColors.fluxSaas : demoColors.devFee,
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.6rem',
                            }}
                        />
                        <Typography sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.8rem',
                            color: retroColors.muted,
                            mb: 1,
                        }}>
                            {stream.label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontWeight: 700,
                                fontSize: '2rem',
                                color: stream.color,
                            }}>
                                {stream.amount}
                            </Typography>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.8rem',
                                color: retroColors.muted,
                            }}>
                                {stream.per}
                            </Typography>
                        </Box>
                        <Typography sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.8rem',
                            color: retroColors.dimmed,
                        }}>
                            {stream.desc}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Value prop comparison */}
            <SplitView
                left={
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(231, 76, 60, 0.05)', border: '1px solid rgba(231, 76, 60, 0.15)' }}>
                        <Typography sx={{ fontFamily: vortxTypography.heading, fontWeight: 600, fontSize: '0.9rem', color: vortxColors.error, mb: 1.5 }}>
                            Without Vortx
                        </Typography>
                        <DemoBullets items={[
                            'Hire power engineers ($450-700K/yr)',
                            'Build or buy EMS software',
                            '15-25% less BESS revenue (unoptimized dispatch)',
                            '10.5-11.5% development yield',
                        ]} color={vortxColors.error} />
                    </Box>
                }
                right={
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(95, 212, 196, 0.05)', border: '1px solid rgba(95, 212, 196, 0.15)' }}>
                        <Typography sx={{ fontFamily: vortxTypography.heading, fontWeight: 600, fontSize: '0.9rem', color: demoColors.actPlatform, mb: 1.5 }}>
                            With Vortx
                        </Typography>
                        <DemoBullets items={[
                            '~12.6% of site gross revenue',
                            'Optimized BESS dispatch from day one',
                            'Grid services enrollment + compliance',
                            '13.5% development yield — net-positive on every site',
                        ]} color={demoColors.actPlatform} />
                    </Box>
                }
            />

            <Typography sx={{
                fontFamily: vortxTypography.body,
                fontSize: '1rem',
                color: vortxColors.white,
                textAlign: 'center',
                mt: 3,
                fontStyle: 'italic',
            }}>
                "We don't own land, batteries, or generators. We own the intelligence layer."
            </Typography>
        </StepSlide>
    );
}

/**
 * Step 5: "Beachhead + Expansion"
 */
export function Step5_BeachheadExpansion({ visible }) {
    const timeline = [
        {
            label: 'NOW',
            title: 'MVP',
            items: ['Site DNA + Flux EMS', 'Julia solver + tariff database', 'AI extraction pipeline'],
            active: true,
        },
        {
            label: 'Q2 2026',
            title: 'Flux EV',
            items: ['Fleet charging coordination', 'EMS gains EV charging agent'],
            active: false,
        },
        {
            label: 'Q3 2026',
            title: 'Flux COLD',
            items: ['Refrigeration scheduling', 'EMS gains cold storage agent'],
            active: false,
        },
        {
            label: 'Q4 2026',
            title: 'Flux PhD',
            items: ['Analytics + dispatch auto-tuning'],
            active: false,
        },
        {
            label: '2027',
            title: 'Gateway + Twin',
            items: ['Hardware dispatch (BACnet/Modbus/OCPP)', 'Digital Twin'],
            active: false,
        },
    ];

    const scaleTrajectory = [
        { year: 'Year 1', sites: '2 sites', team: 'Team of 3', arr: '' },
        { year: 'Year 2', sites: '6 sites', team: 'Team of 5', arr: 'Flux recurring begins' },
        { year: 'Year 3', sites: '70 MW', team: 'Team of 7', arr: '$3M ARR · $2M EBITDA' },
    ];

    return (
        <StepSlide
            title="Beachhead + Expansion"
            subtitle="We start where the demand is most urgent — then the platform goes everywhere."
            keyStat="We're not pitching a roadmap. We're showing you a platform that runs, a beachhead with urgent demand, and unit economics that get better with every site."
            visible={visible}
        >
            <SplitView
                ratio="1fr 1fr"
                left={
                    <Box>
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: vortxColors.gold,
                            mb: 2,
                        }}>
                            Beachhead: AI Infrastructure
                        </Typography>
                        <DemoBullets items={[
                            'AI compute demand growing 20-50% annually',
                            '1-30 MW sites at distribution voltage',
                            'BESS + backup gen + fiber to the meter',
                            '6-18 months to interconnect (vs. 5-7 years)',
                            'We solve the binding constraint: delivering power fast enough',
                        ]} />

                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: demoColors.actPlatform,
                            mt: 3,
                            mb: 2,
                        }}>
                            Expansion: Same Platform, New Verticals
                        </Typography>
                        <DemoBullets items={[
                            'C&I solar + storage (largest distributed energy market)',
                            'Fleet electrification (Flux EV — already built)',
                            'Cold chain facilities (Flux COLD — already built)',
                            'Microgrids and community energy',
                        ]} color={demoColors.actPlatform} />
                    </Box>
                }
                right={
                    <Box>
                        {/* Timeline */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {timeline.map((item, i) => (
                                <Box key={i} sx={{
                                    display: 'flex',
                                    gap: 2,
                                    p: 1.5,
                                    borderRadius: 1,
                                    bgcolor: item.active ? 'rgba(178, 140, 80, 0.08)' : retroColors.surface,
                                    border: `1px solid ${item.active ? vortxColors.gold : 'rgba(178, 140, 80, 0.08)'}`,
                                }}>
                                    <Chip
                                        label={item.label}
                                        size="small"
                                        sx={{
                                            bgcolor: item.active ? vortxColors.gold : 'rgba(136,136,136,0.1)',
                                            color: item.active ? retroColors.bg : retroColors.muted,
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            minWidth: 70,
                                        }}
                                    />
                                    <Box>
                                        <Typography sx={{
                                            fontFamily: vortxTypography.body,
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            color: item.active ? vortxColors.gold : vortxColors.white,
                                        }}>
                                            {item.title}
                                        </Typography>
                                        {item.items.map((sub, j) => (
                                            <Typography key={j} sx={{
                                                fontFamily: vortxTypography.body,
                                                fontSize: '0.75rem',
                                                color: retroColors.muted,
                                            }}>
                                                {sub}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Scale trajectory */}
                        <Box sx={{ mt: 3 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                color: retroColors.muted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.15em',
                                mb: 1,
                            }}>
                                Scale Trajectory
                            </Typography>
                            {scaleTrajectory.map((row, i) => (
                                <Box key={i} sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    py: 0.5,
                                    borderBottom: `1px solid rgba(178, 140, 80, 0.08)`,
                                }}>
                                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.75rem', color: vortxColors.gold }}>
                                        {row.year}
                                    </Typography>
                                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.75rem', color: vortxColors.white }}>
                                        {row.sites} · {row.team}
                                    </Typography>
                                    {row.arr && (
                                        <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.75rem', color: demoColors.actPlatform }}>
                                            {row.arr}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                }
            />
        </StepSlide>
    );
}
